import React, { useState, useEffect, useRef } from 'react';

const BRAIN_URL = "https://brain.gni-ai-solutions.com/api/voice-code";
const AUTH_KEY = "gni-sovereign-secret";

interface VoiceLinkProps {
    onStatusChange?: (status: string) => void;
    onAction?: (action: any) => void;
    onMessage?: (role: string, text: string) => void;
}

export function VoiceLink(props: VoiceLinkProps) {
    const { onStatusChange, onAction } = props;
    const [isListening, setIsListening] = useState(false);
    const [status, setStatus] = useState("Ready");
    const recognitionRef = useRef<any>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        // @ts-ignore
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = false;
            recognition.lang = 'en-US';
            recognition.interimResults = false;

            recognition.onstart = () => {
                setIsListening(true);
                setStatus("Listening...");
            };

            recognition.onresult = (event: any) => {
                const transcript = event.results[0][0].transcript;
                setStatus("Processing...");
                sendToBrain(transcript);
            };

            recognition.onend = () => {
                setIsListening(false);
            };

            recognition.onerror = (event: any) => {
                console.error("Speech Error:", event.error);
                setStatus("Error: " + event.error);
                setIsListening(false);
            };

            recognitionRef.current = recognition;
        }
    }, []);

    const sendToBrain = async (text: string) => {
        try {
            // Log User Message
            if (onStatusChange) onStatusChange("Thinking...");
            // @ts-ignore
            if (props.onMessage) props.onMessage('user', text);

            const response = await fetch(BRAIN_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-gni-key': AUTH_KEY
                },
                body: JSON.stringify({ prompt: text, session_id: "spatial-deck", source: "voice" })
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const data = await response.json();
            console.log("Brain Response:", data);

            // Log Assistant Message
            // @ts-ignore
            if (props.onMessage && data.text) props.onMessage('assistant', data.text);

            // HANDLE ACTION
            if (data.action && onAction) {
                console.log("Executing Action:", data.action);
                onAction(data.action);
            }

            if (data.audio_url) {
                playResponse(data.audio_url);
            } else {
                setStatus("Ready");
                if (onStatusChange) onStatusChange("Ready");
            }

        } catch (error) {
            console.error("Fetch Error:", error);
            setStatus("Connection Failed");
        }
    };

    const playResponse = (url: string) => {
        setStatus("Speaking...");
        if (onStatusChange) onStatusChange("Speaking...");

        const audio = new Audio(url);
        audioRef.current = audio;

        audio.onended = () => {
            setStatus("Ready");
            if (onStatusChange) onStatusChange("Ready");
        };

        audio.play().catch(e => {
            console.error("Autoplay blocked:", e);
            setStatus("Tap to Unmute");
        });
    };

    const startListening = (e: any) => {
        if (e.type === 'touchstart') e.preventDefault();
        try { recognitionRef.current?.start(); } catch (e) { }
    };

    const stopListening = (e: any) => {
        if (e.type === 'touchend') e.preventDefault();
        recognitionRef.current?.stop();
    };

    return (
        <div style={{
            position: 'absolute',
            bottom: '20%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pointerEvents: 'none' // Fix: Allow clicks to pass through transparent areas
        }}>
            {/* BIG BUTTON */}
            <div
                onMouseDown={startListening}
                onMouseUp={stopListening}
                onTouchStart={startListening}
                onTouchEnd={stopListening}
                style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    border: '2px solid #00f3ff',
                    background: isListening ? 'rgba(0, 243, 255, 0.3)' : 'rgba(0, 243, 255, 0.1)',
                    boxShadow: isListening ? '0 0 50px rgba(0, 243, 255, 0.6)' : '0 0 20px rgba(0, 243, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#00f3ff',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.2s ease',
                    transform: isListening ? 'scale(0.95)' : 'scale(1)',
                    pointerEvents: 'auto' // Fix: Re-enable clicks for the button itself
                }}
            >
                {isListening ? "LISTENING" : "SPEAK"}
            </div>

            <div style={{ marginTop: '20px', color: '#00f3ff', fontFamily: 'monospace', textShadow: '0 0 5px #00f3ff' }}>
                [{status.toUpperCase()}]
            </div>
        </div>
    );
}
